<script>
export default {
    props: {
        expression: {
            type: String,
            default: ''
        },
        explanation: {
            type: Object,
            default: null
        }
    },
    computed: {
        interpretation() {
            const expression = (this.expression || '').trim();
            if (!expression) {
                return [];
            }

            const chunks = expression.split(/(&&|\|\|)/).map(c => c.trim()).filter(Boolean);
            const lines = [];

            chunks.forEach((chunk, index) => {
                if (chunk === '&&' || chunk === '||') {
                    lines.push({
                        type: 'operator',
                        text: chunk === '&&' ? 'AND' : 'OR'
                    });
                    return;
                }

                lines.push({
                    type: 'condition',
                    text: this.interpretCondition(chunk, index + 1)
                });
            });

            return lines;
        }
    },
    methods: {
        interpretCondition(raw, index) {
            let condition = raw.trim();
            let negated = false;

            if (condition.startsWith('!')) {
                negated = true;
                condition = condition.slice(1).trim();
            }

            condition = this.unwrapOuterParens(condition);
            const callChain = this.parseCallChain(condition);

            if (!callChain) {
                return `Condition ${index}: ${raw}`;
            }

            const contextApi = this.resolveContext(callChain.context);
            if (!contextApi) {
                return `Condition ${index}: Unknown context "${callChain.context}".`;
            }
            const functionDescriptions = this.resolveFunctionDescriptions(contextApi, callChain.calls);
            const functionText = functionDescriptions.length ? functionDescriptions.join(' ') : callChain.calls.map(c => c.name).join(' -> ');

            const prefix = negated ? 'NOT ' : '';
            return `${prefix}Condition ${index}: ${functionText}`;
        },
        parseCallChain(condition) {
            const contextMatch = condition.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\./);
            if (!contextMatch) {
                return null;
            }

            const context = contextMatch[1];
            const calls = this.extractCalls(condition);
            if (!calls.length) {
                return null;
            }

            return {
                context,
                calls
            };
        },
        unwrapOuterParens(value) {
            let text = (value || '').trim();
            while (text.startsWith('(') && text.endsWith(')')) {
                const closeIndex = this.findClosingParen(text, 0);
                if (closeIndex !== text.length - 1) {
                    break;
                }
                text = text.slice(1, -1).trim();
            }
            return text;
        },
        extractCalls(condition) {
            const calls = [];
            const regex = /\.([a-zA-Z_][a-zA-Z0-9_]*)\s*\(/g;
            let match;

            while ((match = regex.exec(condition)) !== null) {
                const name = match[1];
                const openIndex = condition.indexOf('(', match.index);
                const closeIndex = this.findClosingParen(condition, openIndex);
                if (openIndex < 0 || closeIndex < 0) {
                    break;
                }
                const argsRaw = condition.slice(openIndex + 1, closeIndex).trim();
                calls.push({
                    name,
                    argsRaw,
                    args: this.splitArgs(argsRaw),
                });
                regex.lastIndex = closeIndex + 1;
            }

            return calls;
        },
        findClosingParen(text, openIndex) {
            let depth = 0;
            for (let i = openIndex; i < text.length; i += 1) {
                const char = text[i];
                if (char === '(') {
                    depth += 1;
                } else if (char === ')') {
                    depth -= 1;
                    if (depth === 0) {
                        return i;
                    }
                }
            }
            return -1;
        },
        splitArgs(argsRaw) {
            if (!argsRaw) {
                return [];
            }
            const args = [];
            let current = '';
            let depth = 0;
            for (let i = 0; i < argsRaw.length; i += 1) {
                const char = argsRaw[i];
                if (char === '(') {
                    depth += 1;
                } else if (char === ')') {
                    depth -= 1;
                }
                if (char === ',' && depth === 0) {
                    args.push(current.trim());
                    current = '';
                    continue;
                }
                current += char;
            }
            if (current.trim()) {
                args.push(current.trim());
            }
            return args;
        },
        resolveContext(contextKey) {
            const explanation = this.explanation || {};

            // Main context is a primary API entry point (usually key: "l").
            if (explanation.context && explanation.context.key === contextKey) {
                return explanation.context;
            }

            // Subject API context.
            if (explanation.subject && explanation.subject.key === contextKey) {
                return explanation.subject;
            }

            // Optional module aliases (e.g. pc, productCategory).
            if (explanation.modules) {
                const module = Object.values(explanation.modules).find(m => m.key === contextKey);
                if (module) {
                    return module;
                }
            }
            return null;
        },
        resolveFunctionDescriptions(contextApi, calls) {
            if (!contextApi || !(contextApi.functions instanceof Array)) {
                return [];
            }

            const descriptions = [];
            let currentApi = contextApi;

            calls.forEach(call => {
                const functions = currentApi && currentApi.functions instanceof Array ? currentApi.functions : [];
                const match = functions.find(fn => fn.name === call.name);
                if (!match) {
                    descriptions.push(call.name);
                    currentApi = null;
                    return;
                }

                descriptions.push(this.buildHumanText(match, call.args));
                currentApi = match.return && match.return.functions ? {functions: match.return.functions} : null;
            });

            return descriptions;
        },
        buildHumanText(definition, args) {
            const template = definition.humanTemplate || definition.humanLabel || definition.name;
            const definitionArgs = definition.arguments || [];
            let text = template;

            definitionArgs.forEach((argument, index) => {
                const rawArg = args[index] || argument.name;
                const displayArg = this.renderArgument(argument, rawArg);
                text = text.replace(new RegExp(`\\{${argument.name}\\}`, 'g'), displayArg);
            });

            return text;
        },
        renderArgument(argument, rawArg) {
            if (!rawArg) {
                return argument.argumentDisplay || argument.name;
            }
            if (argument.argumentDisplay && rawArg.includes('subject.')) {
                return argument.argumentDisplay;
            }
            return rawArg;
        }
    }
}
</script>

<template>
    <div v-if="interpretation.length" class="mt-1 border rounded p-1 bg-light">
        <div class="font-weight-bold pb-1">Interpretation</div>
        <div
            v-for="(line, index) in interpretation"
            :key="index"
            class="small"
            :class="line.type === 'operator' ? 'font-weight-bold text-primary py-1' : 'pb-1'"
        >
            {{ line.text }}
        </div>
    </div>
</template>
